import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { useImpression } from '@thd-olt-component-react/impression';
import { Sticky } from '@thd-olt-component-react/sticky';
// Drawer prefs come from the platform session adapter (Seq B.3c). Master reads localStorage
// directly here because it never had the split.
import { useDrawerPrefs, drawerPrefsRecord } from '../../platform/session/session';
import { AssistantIcon } from '../assets/AssistantIcon';
import { ExpandableFab } from './ExpandableFab';
import { getCanonicalPageType } from '../utils/window-utils';
import { FAB_EXPAND_DENYLIST, EXTENDED_DELAY_PAGE_TYPES } from '../assistant-config';

const EXPAND_DELAY = 4000; // ms
const EXTENDED_EXPAND_DELAY = 8000; // ms
const CLOSE_DELAY = 7000; // ms

// MAG4-870: MAA/LC unification test copy. Only the unified experience gets the
// new label — control (legacy) keeps the original. Driven by the same
// `isLiveChatUnificationEnabled` config flag App reads to pick the content tree, so the
// FAB copy always matches which experience is actually mounted.
const FAB_LABEL = 'Magic Apron Assistant';
const UNIFIED_FAB_LABEL = 'Ask a question or get help';

export const AssistantActionButton = ({ onClick, offsetMargin, isUnifiedExperience = false }) => {
  const [forceExpand, setForceExpand] = useState(false);
  // The FAB reads/writes only drawer prefs (which pages the expanded FAB has shown on, and
  // whether the shopper has interacted with the FAB) — the maa-drawer-prefs record (Seq B.3c).
  const [drawerPrefs] = useDrawerPrefs();
  const { lesser: isMobile } = useBreakpoint('lg');

  // `isUnifiedExperience` can flip mid-session — the host fires
  // CONFIG_SERVICE_CHANGE whenever it settles the bucket, not only during initial load.
  // Freeze the copy for the duration of an active auto-reveal so a late flip can't swap the
  // label out from under the user while it's visible; once the reveal closes (or before one
  // starts) the live value is picked back up so the next reveal still reflects a corrected
  // bucket.
  const [committedIsUnifiedExperience, setCommittedIsUnifiedExperience] = useState(
    isUnifiedExperience
  );

  useEffect(() => {
    if (!forceExpand) {
      setCommittedIsUnifiedExperience(isUnifiedExperience);
    }
  }, [isUnifiedExperience, forceExpand]);

  useEffect(() => {
    let openTimeout = null;
    let closeTimeout = null;

    const currentPageType = getCanonicalPageType();
    // Page types are lower case apart from 'Error', and the vocabulary is owned
    // by each experience, so compare on a normalised value.
    const normalisedPageType = String(currentPageType).toLowerCase();

    if (
      !FAB_EXPAND_DENYLIST.includes(normalisedPageType)
      // Sessions before this version might now have the value available so we need to null check
      && !drawerPrefs?.pagesExpandedFABShownOn?.includes(currentPageType)
      && !drawerPrefs?.hasSessionInteractedWithFab
    ) {
      const OPEN_DELAY = EXTENDED_DELAY_PAGE_TYPES.includes(normalisedPageType)
        ? EXTENDED_EXPAND_DELAY : EXPAND_DELAY;
      openTimeout = setTimeout(() => {
        setForceExpand(true);
        // This effect has [] deps but fires 4-8s later, so the closure's `drawerPrefs` is a
        // mount-time snapshot. `update` merges onto the record's CURRENT value instead, so
        // this cannot revert what the Shell (or a peer tab) wrote during the delay.
        drawerPrefsRecord.update((prefs) => ({
          pagesExpandedFABShownOn: [...(prefs?.pagesExpandedFABShownOn || []), currentPageType],
        }));

        closeTimeout = setTimeout(() => setForceExpand(false), CLOSE_DELAY);
      }, OPEN_DELAY);
    }

    return () => {
      clearTimeout(openTimeout);
      clearTimeout(closeTimeout);
    };
  }, []);

  const { ref, clickID } = useImpression({ data: {
    id: '00DEFVALMA00', // *** required prop ***
    component: 'MagicApronAssistant', // *** required prop *** ==> component public export name
    name: 'MagicApronAssistant', // *** required prop *** ==> descriptive titles or component name
    position: '1',
    type: 'content',
    category: '', // Should be empty string per spec
  } });

  const fabContainerClasses = classNames(
    'sui-z-max sui-bottom-4 sui-right-2',
    {
      'sui-absolute': isMobile,
      'sui-fixed xl:sui-bottom-10 xl:sui-right-10': !isMobile,
    }
  );

  const stickyClassnames = classNames('sui-w-full sui-transition-[bottom]', {
    '!sui-duration-500': offsetMargin > 0
  });

  return (
    <Sticky
      className={stickyClassnames}
      position="bottom"
      stickyByDefault
      visibleByDefault
      offsetMargin={offsetMargin}
    >
      <div className={fabContainerClasses} data-component="AssistantActionButton">
        <ExpandableFab
          aria-label="Magic Apron Assistant Open Button"
          onClick={onClick}
          ref={ref}
          clickid={clickID}
          collapsible
          forceExpand={forceExpand}
          label={committedIsUnifiedExperience ? UNIFIED_FAB_LABEL : FAB_LABEL}
        >
          <AssistantIcon color="brand" height="50%" width="24px" margin="2px" />
        </ExpandableFab>
      </div>
    </Sticky>
  );
};

AssistantActionButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  offsetMargin: PropTypes.number.isRequired,
  isUnifiedExperience: PropTypes.bool,
};
